import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type CurrentUser } from './lib/api';
import { ProductTable } from './components/ProductTable';
import { LinkManager } from './components/LinkManager';
import { HistoryTab } from './components/HistoryTab';
import { PendingChanges } from './components/PendingChanges';
import { AlertBanner } from './components/AlertBanner';
import { PendingBar } from './components/PendingBar';
import { Admin } from './components/Admin';
import { SheetSyncTab } from './components/SheetSyncTab';

// The charting library only loads for someone who opens Sales.
const SalesTab = lazy(() => import('./sales/SalesTab'));

type Tab = 'products' | 'sales' | 'pending' | 'inactive' | 'links' | 'history' | 'sheet' | 'admin';

/** A link to #sales… (from an email report, say) opens straight on the Sales tab. */
const initialTab = (): Tab => (window.location.hash.startsWith('#sales') ? 'sales' : 'products');

export function App() {
  const queryClient = useQueryClient();
  const [tab, setTabState] = useState<Tab>(initialTab);
  // Only the Sales tab keeps state in the URL; leaving it clears the hash so a
  // reload lands where the user is, not back on Sales.
  const setTab = (next: Tab) => {
    if (next !== 'sales' && window.location.hash.startsWith('#sales')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    setTabState(next);
  };
  const chromeRef = useRef<HTMLDivElement>(null);

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    retry: false,
    // A 401 is the normal not-logged-in state, not an error worth retrying.
    throwOnError: false,
  });

  // Shared with PendingBar and PendingChanges by query key, so this costs no
  // extra request — just a count for the nav pill.
  const queueQuery = useQuery({
    queryKey: ['queue'],
    queryFn: api.queue,
    enabled: !!meQuery.data && !meQuery.data.mustChangePassword,
    throwOnError: false,
  });

  /**
   * Keeps --app-chrome-height (styles.css) matched to the sticky header+bar
   * stack's real rendered height, so .table-wrap's max-height leaves exactly
   * enough room under it. A static guess breaks the moment a long email or a
   * big conflict/owner summary wraps the bar onto a second line — this
   * re-measures whenever that box actually changes size, rather than
   * guessing a number up front.
   *
   * Declared before the early returns below so the hook itself always runs
   * (rules of hooks); the ref just has nothing to attach to until the chrome
   * div is actually in the DOM, which is what the dependency below is for —
   * it flips once login/password-reset clears and the effect re-attaches.
   */
  const showChrome = !!meQuery.data && !meQuery.data.mustChangePassword;
  useEffect(() => {
    const node = chromeRef.current;
    if (!node) return;
    const setHeight = () => {
      document.documentElement.style.setProperty('--app-chrome-height', `${node.getBoundingClientRect().height}px`);
    };
    setHeight();
    const observer = new ResizeObserver(setHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [showChrome]);

  if (meQuery.isLoading) return <div className="empty-state">Loading…</div>;

  const user = meQuery.data;
  if (!user) return <Login onSignedIn={() => void queryClient.invalidateQueries({ queryKey: ['me'] })} />;

  // A user mid-reset can reach nothing else until the password is changed.
  if (user.mustChangePassword) {
    return <ChangePassword onDone={() => void queryClient.invalidateQueries({ queryKey: ['me'] })} />;
  }

  return (
    <>
      {/* Sticky as one unit (see .app-chrome in styles.css) — the bar's
          height isn't fixed (it varies with conflict/owner text), so it
          can't carry its own `top` offset independent of the header. */}
      <div className="app-chrome" ref={chromeRef}>
        <header className="app-header">
          <h1>MFS Price Manager</h1>
          <nav>
            <button className={tab === 'products' ? 'active' : ''} onClick={() => setTab('products')}>
              Products
            </button>
            <button className={tab === 'sales' ? 'active' : ''} onClick={() => setTab('sales')}>
              Sales
            </button>
            {/* Next to Products deliberately: the queue is where an edit made
                in the table goes, so the two belong side by side. */}
            <button className={tab === 'pending' ? 'active' : ''} onClick={() => setTab('pending')}>
              Pending
              {queueQuery.data && queueQuery.data.total > 0 && (
                <span className="pill">{queueQuery.data.total}</span>
              )}
            </button>
            <button className={tab === 'inactive' ? 'active' : ''} onClick={() => setTab('inactive')}>
              Inactive Listings
            </button>
            <button className={tab === 'links' ? 'active' : ''} onClick={() => setTab('links')}>
              Links
            </button>
            <button className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>
              History
            </button>
            <button className={tab === 'sheet' ? 'active' : ''} onClick={() => setTab('sheet')}>
              Sheet Sync
            </button>
            {user.role === 'admin' && (
              <button className={tab === 'admin' ? 'active' : ''} onClick={() => setTab('admin')}>
                Admin
              </button>
            )}
          </nav>
          <div className="spacer" />
          <span className="small muted">
            {user.email} · {user.role}
          </span>
          <button
            onClick={async () => {
              await api.logout();
              queryClient.clear();
            }}
          >
            Sign out
          </button>
        </header>

        {/* The one place to push the queue, reachable from every tab — see
            PendingBar's own doc comment. */}
        <PendingBar onOpenPending={() => setTab('pending')} />
      </div>

      <main className="page">
        {/* Above the tabs, so a stale channel is seen before its prices are
            read rather than after. */}
        <AlertBanner onOpenAdmin={user.role === 'admin' ? () => setTab('admin') : undefined} />
        {tab === 'products' && <ProductTable user={user} status="active" />}
        {tab === 'sales' && (
          <Suspense fallback={<div className="empty-state">Loading sales…</div>}>
            <SalesTab />
          </Suspense>
        )}
        {/* Same table, filtered to what the channels are withholding. Keyed so
            switching tabs remounts rather than reusing the other tab's state. */}
        {tab === 'inactive' && <ProductTable key="inactive" user={user} status="inactive" />}
        {tab === 'links' && <LinkManager user={user} />}
        {tab === 'history' && <HistoryTab />}
        {tab === 'pending' && (
          <PendingChanges canEdit={user.role === 'admin' || user.role === 'editor'} />
        )}
        {tab === 'sheet' && <SheetSyncTab user={user} />}
        {tab === 'admin' && user.role === 'admin' && <Admin user={user} />}
      </main>
    </>
  );
}

function Login({ onSignedIn }: { onSignedIn: (user: CurrentUser) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: () => api.login(email, password),
    onSuccess: onSignedIn,
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="login-wrap">
      <form
        className="card login-card"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          login.mutate();
        }}
      >
        <h1>MFS Price Manager</h1>
        <p className="small muted" style={{ marginTop: 0 }}>Sign in to continue</p>

        {error && <div className="alert error">{error}</div>}

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <button className="primary" type="submit" disabled={login.isPending} style={{ width: '100%' }}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

function ChangePassword({ onDone }: { onDone: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () => api.changePassword(currentPassword, newPassword),
    onSuccess: onDone,
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="login-wrap">
      <form
        className="card login-card"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (newPassword !== confirm) {
            setError('The two new passwords do not match');
            return;
          }
          if (newPassword.length < 12) {
            setError('New password must be at least 12 characters');
            return;
          }
          change.mutate();
        }}
      >
        <h1>Choose a new password</h1>
        <p className="small muted" style={{ marginTop: 0 }}>
          Your account uses a one-time password and must be changed before you continue.
        </p>

        {error && <div className="alert error">{error}</div>}

        <div className="field">
          <label>Current (one-time) password</label>
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label>New password</label>
          <input
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label>Confirm new password</label>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>

        <button className="primary" type="submit" disabled={change.isPending} style={{ width: '100%' }}>
          {change.isPending ? 'Saving…' : 'Set password'}
        </button>
      </form>
    </div>
  );
}
